import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ASSETS, PRACTICE } from "@/data/site";

export default function Features() {
  return (
    <section
      data-testid="features-section"
      className="px-6 md:px-10 lg:px-16 py-20 md:py-28 space-y-20 md:space-y-28 overflow-hidden"
    >
      {/* Invisalign */}
      <FeatureBlock
        eyebrow="Featured Care"
        title={
          <>
            Straighten your smile, <span className="italic text-[var(--accent)]">invisibly.</span>
          </>
        }
        body="Invisalign® clear aligners gently shift your teeth into place without the wires, brackets or 'I'm in braces' announcement. Custom-made for your bite, swapped every 1–2 weeks, removable for meals and brushing."
        image={ASSETS.invisalign}
        ctaText="Learn about Invisalign"
        ctaTo="/services/invisalign"
        secondaryText="Request consult"
        secondaryHref={PRACTICE.appointmentUrl}
        reverse={false}
        testid="feature-invisalign"
      />

      {/* Botox */}
      <FeatureBlock
        eyebrow="Discover Your Brilliance"
        title={
          <>
            BOTOX® and JUVÉDERM® — <span className="italic text-[var(--accent)]">subtle</span>, refreshed.
          </>
        }
        body="The same precise hands that craft your smile can soften lines around it. Dr. Yopa offers facial aesthetic treatments designed to complement — never compete with — your natural features."
        image={ASSETS.botox}
        ctaText="Explore facial aesthetics"
        ctaTo="/facial-aesthetics"
        secondaryText="$129 New Patient Special"
        secondaryHref="#contact"
        reverse
        testid="feature-botox"
      />
    </section>
  );
}

function FeatureBlock({
  eyebrow,
  title,
  body,
  image,
  ctaText,
  ctaTo,
  secondaryText,
  secondaryHref,
  reverse,
  testid,
}) {
  return (
    <div
      data-testid={testid}
      className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center`}
    >
      <motion.div
        initial={{ opacity: 0, x: reverse ? 30 : -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7 }}
        className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}
      >
        <div className="arch-shape aspect-[3/4] w-full max-w-[440px] mx-auto bg-[var(--surface)]">
          <img
            src={image}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7 }}
        className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}
      >
        <div className="font-mono-eyebrow text-[var(--text-muted)] flex items-center gap-3">
          <span className="w-8 h-px bg-[var(--accent)]" />
          {eyebrow}
        </div>
        <h2 className="font-serif-display text-[var(--primary)] mt-5 text-[40px] md:text-[60px] leading-[1.02] tracking-tight">
          {title}
        </h2>
        <p className="mt-6 text-[17px] text-[var(--text-muted)] leading-relaxed max-w-xl">
          {body}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to={ctaTo}
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--primary)] text-[var(--bg)] hover:bg-black"
          >
            {ctaText} <ArrowUpRight className="w-4 h-4" />
          </Link>
          <a
            href={secondaryHref}
            target={secondaryHref?.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer"
            className="inline-flex items-center px-7 py-4 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)]"
          >
            {secondaryText}
          </a>
        </div>
      </motion.div>
    </div>
  );
}
